import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import apiClient from '../services/apiClient';

/**
 * AdminNavContext — a single source of truth for "where am I in the content tree?"
 *
 * It watches the URL, figures out the current path (and story, if any), fetches
 * that path's ordered stories ONCE, and shares the result with:
 *   - the admin sidebar (contextual "Current path" story list)
 *   - the breadcrumb bar
 *   - the StoryPathNav (prev / next / jump between stories)
 *
 * Everything downstream reads from here so we never re-fetch the same path in
 * three different components.
 */
const AdminNavContext = createContext({
  path: null,
  stories: [],
  storyId: null,
  currentIndex: -1,
  prevStory: null,
  nextStory: null,
  loading: false,
});

export const useAdminNav = () => useContext(AdminNavContext);

const cleanParam = (value) =>
  value && value !== 'new' && value !== 'edit' ? value : null;

export const AdminNavProvider = ({ children }) => {
  const location = useLocation();

  const [state, setState] = useState({
    path: null,
    stories: [],
    storyId: null,
    loading: false,
  });

  // Derive the current story / path id from the URL.
  const storyMatch =
    matchPath('/admin/stories/:storyId/*', location.pathname) ||
    matchPath('/admin/stories/:storyId', location.pathname);
  const pathMatch =
    matchPath('/admin/paths/:pathId/*', location.pathname) ||
    matchPath('/admin/paths/:pathId', location.pathname);

  const storyId = cleanParam(storyMatch?.params?.storyId);
  const pathIdFromUrl = cleanParam(pathMatch?.params?.pathId);

  useEffect(() => {
    let cancelled = false;

    const reset = () =>
      setState({ path: null, stories: [], storyId: null, loading: false });

    const load = async () => {
      setState((s) => ({ ...s, storyId, loading: true }));

      let pathId = pathIdFromUrl;
      let story = null;

      // If we're on a story route, resolve its parent path first.
      if (storyId) {
        try {
          const res = await apiClient.admin.getStory(storyId);
          story = res.data.story;
          pathId = story?.path_id || story?.path?.path_id || pathId;
        } catch (e) {
          /* non-fatal — the page itself will surface the error */
        }
      }

      if (!pathId) {
        if (!cancelled) setState({ path: null, stories: [], storyId, loading: false });
        return;
      }

      try {
        const [pathRes, storiesRes] = await Promise.all([
          apiClient.admin.getPath(pathId),
          apiClient.admin.getPathStories(pathId),
        ]);
        if (cancelled) return;

        const path = pathRes.data?.path || pathRes.data || null;
        const stories = (storiesRes.data?.stories || storiesRes.data || [])
          .slice()
          .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

        setState({ path, stories, storyId, loading: false });
      } catch (e) {
        if (!cancelled) setState({ path: null, stories: [], storyId, loading: false });
      }
    };

    if (storyId || pathIdFromUrl) {
      load();
    } else {
      reset();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId, pathIdFromUrl]);

  const currentIndex = state.storyId
    ? state.stories.findIndex((s) => s.story_id === state.storyId)
    : -1;
  const prevStory = currentIndex > 0 ? state.stories[currentIndex - 1] : null;
  const nextStory =
    currentIndex >= 0 && currentIndex < state.stories.length - 1
      ? state.stories[currentIndex + 1]
      : null;

  return (
    <AdminNavContext.Provider
      value={{ ...state, currentIndex, prevStory, nextStory }}
    >
      {children}
    </AdminNavContext.Provider>
  );
};

export default AdminNavContext;
